import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Plus, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductsPage() {
  const { t, getCategoryName, getProductName } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [form, setForm] = useState({
    category_id: '',
    product_name_ko: '',
    product_name_ru: '',
    product_name_en: '',
    product_name_uz: '',
    description: '',
    unit_type: 'box',
    packs_per_box: 1,
    base_price: 0,
    available_shipping_days: ['monday', 'thursday'],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [pRes, cRes] = await Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/product-categories`),
    ]);
    setProducts(pRes.data);
    setCategories(cRes.data);
    if (cRes.data.length > 0 && !form.category_id) {
      setForm(f => ({ ...f, category_id: cRes.data[0].id }));
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/products`, form);
      toast.success('Product created');
      setDialogOpen(false);
      setForm({
        category_id: categories[0]?.id || '',
        product_name_ko: '', product_name_ru: '', product_name_en: '', product_name_uz: '',
        description: '', unit_type: 'box', packs_per_box: 1, base_price: 0,
        available_shipping_days: ['monday', 'thursday'],
      });
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const formatCurrency = (val) => `₩${Number(val).toLocaleString('ko-KR')}`;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('products')}</h1>
          <p className="text-gray-500 mt-1">{products.length} products</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('add')} {t('products')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>{t('productCategories')}</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({...form, category_id: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{getCategoryName(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name 한국어</Label>
                  <Input value={form.product_name_ko} onChange={(e) => setForm({...form, product_name_ko: e.target.value})} data-testid="product-name-ko" />
                </div>
                <div>
                  <Label>Name Русский</Label>
                  <Input value={form.product_name_ru} onChange={(e) => setForm({...form, product_name_ru: e.target.value})} />
                </div>
                <div>
                  <Label>Name English</Label>
                  <Input value={form.product_name_en} onChange={(e) => setForm({...form, product_name_en: e.target.value})} />
                </div>
                <div>
                  <Label>Name O'zbekcha</Label>
                  <Input value={form.product_name_uz} onChange={(e) => setForm({...form, product_name_uz: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit_type} onValueChange={(v) => setForm({...form, unit_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="box">Box</SelectItem>
                      <SelectItem value="pack">Pack</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Packs/Box</Label>
                  <Input type="number" value={form.packs_per_box} onChange={(e) => setForm({...form, packs_per_box: parseInt(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>{t('price')}</Label>
                  <Input type="number" value={form.base_price} onChange={(e) => setForm({...form, base_price: parseFloat(e.target.value) || 0})} data-testid="product-price" />
                </div>
              </div>
              <div>
                <Label>Available Shipping Days</Label>
                <div className="flex gap-2 mt-2">
                  {['monday', 'thursday'].map(day => (
                    <label key={day} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.available_shipping_days.includes(day)}
                        onChange={(e) => {
                          const days = e.target.checked
                            ? [...form.available_shipping_days, day]
                            : form.available_shipping_days.filter(d => d !== day);
                          setForm({...form, available_shipping_days: days});
                        }}
                      />
                      {t(day)}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-product-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map(p => (
          <Card key={p.id} className="p-5 cff-hover-lift" data-testid={`product-card-${p.id}`}>
            <div className="flex items-start gap-2 mb-3">
              <div className="bg-orange-50 p-2 rounded-md">
                <Package className="h-4 w-4 text-[#556B2F]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{getProductName(p)}</p>
                <p className="text-xs text-gray-500">{getCategoryName(p.category)}</p>
              </div>
            </div>
            <div className="text-sm space-y-1">
              <p className="text-xl font-bold text-gray-900 cff-text-numeric">{formatCurrency(p.base_price)}</p>
              <p className="text-xs text-gray-500">
                {p.unit_type} · {p.packs_per_box} packs · Ships: {(p.available_shipping_days || []).join(', ')}
              </p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
